import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { LogbookEntry, UpsertLogbookEntryRequest } from 'shared';
import { logbookApi, logbookErrorMessage, logbookImageUrl } from '../api/logbook';

const emptyForm: UpsertLogbookEntryRequest = { date: '', title: '', text: '', location: '' };

function toFormState(entry: LogbookEntry): UpsertLogbookEntryRequest {
  return {
    date: entry.date,
    title: entry.title,
    text: entry.text,
    location: entry.location ?? '',
  };
}

export const LogbookManagement: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [entries, setEntries] = useState<LogbookEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogEntry, setDialogEntry] = useState<LogbookEntry | null>(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [form, setForm] = useState<UpsertLogbookEntryRequest>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [removingImage, setRemovingImage] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setEntries(await logbookApi.list());
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Fehler',
        detail: logbookErrorMessage(err, 'Logbuch konnte nicht geladen werden'),
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const clearPendingImage = () => {
    if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
    setPendingImageFile(null);
    setPendingImagePreview(null);
  };

  const openCreate = () => {
    setDialogEntry(null);
    setForm(emptyForm);
    clearPendingImage();
    setDialogVisible(true);
  };

  const openEdit = (entry: LogbookEntry) => {
    setDialogEntry(entry);
    setForm(toFormState(entry));
    clearPendingImage();
    setDialogVisible(true);
  };

  const closeDialog = () => {
    setDialogVisible(false);
    setDialogEntry(null);
    clearPendingImage();
  };

  const handleSave = async () => {
    if (!form.date || !form.title.trim() || !form.text.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Unvollständig',
        detail: 'Datum, Titel und Text sind erforderlich',
        life: 4000,
      });
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form, location: form.location?.trim() || undefined };
      const wasNew = !dialogEntry;
      const saved = dialogEntry
        ? await logbookApi.update(dialogEntry.id, payload)
        : await logbookApi.create(payload);

      if (pendingImageFile) {
        await logbookApi.uploadImage(saved.id, pendingImageFile);
      }

      toast.current?.show({
        severity: 'success',
        summary: wasNew ? 'Erstellt' : 'Aktualisiert',
        detail: `"${form.title}" wurde gespeichert`,
        life: 3000,
      });
      reload();
      closeDialog();
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Fehler',
        detail: logbookErrorMessage(err, 'Eintrag konnte nicht gespeichert werden'),
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview);
    setPendingImageFile(file);
    setPendingImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = async () => {
    // a not-yet-uploaded selection just gets cleared locally
    if (pendingImageFile) {
      clearPendingImage();
      return;
    }
    if (!dialogEntry?.image) return;

    setRemovingImage(true);
    try {
      const updated = await logbookApi.removeImage(dialogEntry.id);
      setDialogEntry(updated);
      reload();
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Fehler',
        detail: logbookErrorMessage(err, 'Bild konnte nicht entfernt werden'),
        life: 5000,
      });
    } finally {
      setRemovingImage(false);
    }
  };

  const handleDelete = (entry: LogbookEntry) => {
    confirmDialog({
      message: `"${entry.title}" wirklich löschen? Das Löschen kann nicht rückgängig gemacht werden.`,
      header: 'Eintrag löschen',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Löschen',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await logbookApi.remove(entry.id);
          toast.current?.show({ severity: 'success', summary: 'Gelöscht', life: 3000 });
          reload();
        } catch (err: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Fehler',
            detail: logbookErrorMessage(err, 'Eintrag konnte nicht gelöscht werden'),
            life: 5000,
          });
        }
      },
    });
  };

  const dateBodyTemplate = (entry: LogbookEntry) =>
    new Date(entry.date).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });

  const imageBodyTemplate = (entry: LogbookEntry) => {
    const url = logbookImageUrl(entry);
    return url ? (
      <img src={url} alt="" className="w-12 h-12 object-cover border border-gray-300" />
    ) : (
      <span className="text-gray-300">
        <i className="pi pi-image text-xl" />
      </span>
    );
  };

  const actionsBodyTemplate = (entry: LogbookEntry) => (
    <div className="flex gap-1 justify-end">
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-text"
        tooltip="Bearbeiten"
        tooltipOptions={{ position: 'top' }}
        onClick={() => openEdit(entry)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-text p-button-danger"
        tooltip="Löschen"
        tooltipOptions={{ position: 'left' }}
        onClick={() => handleDelete(entry)}
      />
    </div>
  );

  return (
    <div data-component="logbook-management">
      <Toast ref={toast} />
      <ConfirmDialog data-component="logbook-confirm-dialog" />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold">Logbuch</h2>
          <p className="text-sm text-gray-500">
            Einträge erscheinen auf der Homepage unter /aktivitaeten und in der "Aktuelles"-Vorschau
          </p>
        </div>
        <Button label="Neuer Eintrag" icon="pi pi-plus" onClick={openCreate} />
      </div>

      {loading ? (
        <div className="flex flex-col gap-2" data-component="logbook-skeleton">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} height="3rem" />
          ))}
        </div>
      ) : (
        <DataTable
          value={entries}
          paginator
          rows={20}
          className="p-datatable-sm"
          showGridlines
          stripedRows
          emptyMessage="Noch keine Einträge"
          sortField="date"
          sortOrder={-1}
        >
          <Column header="Bild" body={imageBodyTemplate} style={{ width: '5rem' }} />
          <Column header="Datum" body={dateBodyTemplate} sortable sortField="date" style={{ minWidth: '10rem' }} />
          <Column field="title" header="Titel" sortable style={{ minWidth: '16rem' }} />
          <Column field="location" header="Ort" style={{ minWidth: '8rem' }} />
          <Column header="Aktionen" body={actionsBodyTemplate} style={{ width: '8rem' }} />
        </DataTable>
      )}

      <Dialog
        visible={dialogVisible}
        onHide={closeDialog}
        header={dialogEntry ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}
        style={{ width: '44rem', maxWidth: '95vw' }}
        modal
      >
        <div className="flex flex-col gap-4">
          <div>
            <label htmlFor="logbook-date" className="block text-sm font-medium mb-1">Datum</label>
            <Calendar
              id="logbook-date"
              value={form.date ? new Date(form.date) : null}
              onChange={(e) => setForm({ ...form, date: e.value ? (e.value as Date).toISOString().slice(0, 10) : '' })}
              dateFormat="dd.mm.yy"
              showIcon
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor="logbook-title" className="block text-sm font-medium mb-1">Titel</label>
            <InputText
              id="logbook-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full"
              maxLength={200}
            />
          </div>
          <div>
            <label htmlFor="logbook-location" className="block text-sm font-medium mb-1">Ort (optional)</label>
            <InputText
              id="logbook-location"
              value={form.location ?? ''}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="w-full"
              maxLength={200}
            />
          </div>
          <div>
            <label htmlFor="logbook-text" className="block text-sm font-medium mb-1">Text</label>
            <InputTextarea
              id="logbook-text"
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              rows={5}
              autoResize
              className="w-full"
              maxLength={5000}
            />
          </div>

          {(() => {
            const previewUrl = pendingImagePreview ?? (dialogEntry ? logbookImageUrl(dialogEntry) : null);
            const hasImage = Boolean(pendingImageFile || dialogEntry?.image);
            return (
              <div>
                <span className="block text-sm font-medium mb-1">Bild (optional)</span>
                {previewUrl && (
                  <img src={previewUrl} alt="" className="w-full h-40 object-cover border border-gray-300 mb-2" />
                )}
                <input
                  ref={fileInputRef}
                  id="logbook-image-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleImageSelect}
                />
                <div className="flex gap-2">
                  {/* a <label for=...> triggers the file picker natively — more
                      reliable than a PrimeReact Button forwarding a click to a
                      ref, which can lose the click's "user activation" */}
                  <label
                    htmlFor="logbook-image-input"
                    className="p-button p-button-outlined inline-flex items-center gap-2 cursor-pointer"
                  >
                    <i className="pi pi-image" />
                    <span>{hasImage ? 'Bild ersetzen' : 'Bild hochladen'}</span>
                  </label>
                  {hasImage && (
                    <Button
                      label="Entfernen"
                      icon="pi pi-times"
                      className="p-button-outlined p-button-danger"
                      onClick={handleRemoveImage}
                      loading={removingImage}
                    />
                  )}
                </div>
              </div>
            );
          })()}

          <div className="flex justify-end gap-2">
            <Button label="Abbrechen" className="p-button-text" onClick={closeDialog} disabled={saving} />
            <Button label="Speichern" icon="pi pi-check" onClick={handleSave} loading={saving} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};
